import { Dialog } from 'primereact/dialog';
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis, YAxis
} from 'recharts';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  calculate_revenue_trends,
  GET_CURRENCIES_URL,
  getAllBookingPropertiesURL,
  getAllReservationsURL,
  getBookingDashboardStatsURL,
  getReservationTrendURL
} from "../../../utils/urls";
import Layout from "../component/layout";
import BookingModal from './booking_modal';
import CheckoutModal from "./checkout_modal";
import EditBookingPropertyForm from './edit_booking_property';
import ReservationDetailsModal from './reservation_details';
import "./styles.css";

const BookingDashboard = () => {
  const navigate = useNavigate();
  const statsRefs = useRef({});

  // State management
  const [loading, setLoading] = useState(true);
  const [configStatus, setConfigStatus] = useState({});
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [units, setUnits] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [selectedPropertyToEdit, setSelectedPropertyToEdit] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [facilityIdForEdit, setFacilityIdForEdit] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({
    totalUnits: 0,
    listedUnits: 0,
    totalReservations: 0,
    pendingReservations: 0,
    confirmedReservations: 0,
    cancelledReservations: 0,
    completedReservations: 0,
    occupancyRate: 0,
    bookedUnitsCount: 0
  });

  const [stats, setStats] = useState({
    totalUnits: 0,
    listedUnits: 0,
    totalReservations: 0,
    pendingReservations: 0,
    confirmedReservations: 0,
    cancelledReservations: 0,
    completedReservations: 0,
    occupancyRate: 0,
    bookedUnitsCount: 0
  });

  const [revenueData, setRevenueData] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [reservationTrend, setReservationTrend] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [showAnimation, setShowAnimation] = useState(true);
  const [revenuePeriod, setRevenuePeriod] = useState('monthly');
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [currentDate] = useState(new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }));

  const normalizeReservationStatus = (status) => {
    const statusMap = {
      new: 'reserved',
      confirmed: 'booked',
      cancelled: 'canceled',
      canceled: 'canceled',
      reserved: 'reserved',
      booked: 'booked',
      completed: 'completed'
    };

    return statusMap[status] || status;
  };

  const openBookingModal = (unit) => {
    // Check if unit is already booked and not checked out
    const isUnitBooked = reservations.some(res =>
      normalizeId(res.unitId) === normalizeId(unit.unit.id) &&
      ['reserved', 'booked'].includes(normalizeReservationStatus(res.status))
    );

    if (isUnitBooked) {
      toastify("This apartment is currently booked and cannot be reserved until checkout is complete", "warning");
      return;
    }

    const formattedUnit = {
      unit: {
        id: unit.unit.id || unit.unit._id,
        name: unit.unit.name,
        unitType: unit.unit.unitType || 'Unknown',
      },
      bookingProperty: {
        id: unit.bookingProperty?.id || unit.bookingProperty?._id,
        propertyName: unit.bookingProperty?.propertyName || unit.unit.name,
        basePrice: unit.bookingProperty?.basePrice || 0,
        currencyId: unit.bookingProperty?.currencyId,
        managedByLandlord: unit.bookingProperty?.managedByLandlord || false,
        ...unit.bookingProperty
      }
    };

    setSelectedUnit(formattedUnit);
    setShowBookingModal(true);
  };

  const openDetailsModal = (reservation) => {
    setSelectedReservationId(reservation._id || reservation.bookingReservationId);
    setShowDetailsModal(true);
  };

  const openCheckoutModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowCheckoutModal(true);
  };

  const openEditPropertyModal = async (unit) => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("Error: Facility ID not found", "error");
        return;
      }

      setFacilityIdForEdit(facilityId);

      // If currencies haven't been loaded yet, load them
      if (currencies.length === 0) {
        await fetchCurrencies(facilityId);
      }

      const formattedUnit = {
        bookingProperty: {
          ...unit.bookingProperty,
          basePrice: Number(unit.bookingProperty.basePrice || 0),
          propertyName: unit.bookingProperty.propertyName || unit.unit.name,
          propertyType: unit.bookingProperty.propertyType || unit.unit.unitType
        },
        unit: unit.unit
      };

      setSelectedPropertyToEdit(formattedUnit);
      setShowEditPropertyModal(true);
    } catch (error) {
      console.error("Error preparing edit modal:", error);
      toastify("Error preparing edit form", "error");
    }
  };

  const handleBookingSuccess = async (bookingData) => {
    toastify("Booking created successfully", "success");

    try {
      setShowAnimation(true);
      await Promise.all([
        fetchRecentReservations(),
        fetchDashboardStats()
      ]);

      import('./reservation-trend-service').then(module => {
        const reservationTrendService = module.default;
        const statusForTrend = normalizeReservationStatus(bookingData?.status || 'reserved');
        reservationTrendService.updateTrendData(new Date(), statusForTrend);
      });
    } catch (error) {
      console.error("Error updating dashboard after booking:", error);
    }
  };

  const handleEditPropertySuccess = (updatedProperty) => {
    setShowEditPropertyModal(false);
    loadDashboardData();
    toastify("Property updated successfully", "success");
  };

  const fetchCurrencies = async (facilityId) => {
    try {
      if (!facilityId) {
        facilityId = await getItem("selectedFacilityId");
      }

      if (!facilityId) {
        console.error("No facility ID found");
        return;
      }

      const response = await makeRequest2(
        `${GET_CURRENCIES_URL}/${facilityId}`,
        "GET"
      );

      if (response.success) {
        let currencyData = [];

        // Handle different API response formats
        if (Array.isArray(response.data)) {
          currencyData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          currencyData = response.data.data;
        } else if (response.data && response.data.currencies && Array.isArray(response.data.currencies)) {
          currencyData = response.data.currencies;
        }

        const processedCurrencies = currencyData.map(currency => ({
          _id: currency._id || currency.id,
          name: currency.currencyName || currency.name || `${currency.currencyShortCode || currency.code}`,
          code: currency.currencyShortCode || currency.code || 'USD',
          symbol: currency.symbol || '$'
        }));

        if (processedCurrencies.length === 0) {
          processedCurrencies.push({
            _id: "default-usd",
            name: "US Dollar",
            code: "USD",
            symbol: "$"
          });
        }

        setCurrencies(processedCurrencies);
        return processedCurrencies;
      } else {
        throw new Error(response.error || "Failed to fetch currencies");
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);

      const defaultCurrencies = [
        { _id: "default-usd", name: "US Dollar", code: "USD", symbol: "$" },
        { _id: "default-eur", name: "Euro", code: "EUR", symbol: "€" }
      ];

      setCurrencies(defaultCurrencies);
      return defaultCurrencies;
    }
  };

  // Fetch dashboard statistics using the correct endpoint
  const fetchDashboardStats = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      // Use the correct dashboard stats endpoint
      const response = await makeRequest2(
        `${getBookingDashboardStatsURL}/${facilityId}`,
        "GET"
      );

      if (response.success) {
        const dashboardData = response.data?.data || response.data || {};

        // Extract stats from the API response structure
        const unitsData = dashboardData.units || {};
        const reservationsData = dashboardData.reservations || {};
        const revenueData = dashboardData.revenue || {};
        const chartsData = dashboardData.charts || {};

        // Update stats with correct API data
        const reservedCount = reservationsData.reservedReservations ?? reservationsData.pendingReservations ?? 0;
        const bookedCount = reservationsData.bookedReservations ?? reservationsData.confirmedReservations ?? 0;
        const canceledCount = reservationsData.canceledReservations ?? reservationsData.cancelledReservations ?? 0;

        setStats({
          totalUnits: unitsData.totalUnits || 0,
          listedUnits: unitsData.totalListedProperties || 0,
          totalReservations: reservationsData.totalReservations || 0,
          pendingReservations: reservedCount,
          confirmedReservations: bookedCount,
          cancelledReservations: canceledCount,
          completedReservations: reservationsData.completedReservations || 0,
          occupancyRate: unitsData.occupancyRate || 0,
          // Calculate booked units from active reservations
          bookedUnitsCount: reservedCount + bookedCount
        });

        // Set reservation trend data from charts
        if (chartsData.weeklyTrend && Array.isArray(chartsData.weeklyTrend)) {
          setReservationTrend(chartsData.weeklyTrend);
        } else {
          setReservationTrend(generateFallbackReservationTrend());
        }

        // Set pie chart data for units
        const totalUnits = unitsData.totalUnits || 0;
        const listedUnits = unitsData.totalListedProperties || 0;

        setPieData([
          { name: 'Listed', value: listedUnits, color: '#4CAF50' },
          { name: 'Available (Not Listed)', value: Math.max(0, totalUnits - listedUnits), color: '#FFC107' }
        ]);

      } else {
        throw new Error(response.error || "Failed to fetch dashboard stats");
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setReservationTrend(generateFallbackReservationTrend());
    }
  };

  const handleTrendPeriodChange = async (e) => {
    const period = e.target.value;
    await fetchReservationTrend(period);
  };

  const handleRevenuePeriodChange = async (period) => {
    setRevenuePeriod(period);
    setRevenueLoading(true);

    try {
      const facilityId = await getItem("selectedFacilityId");

      // Set empty data first for smooth transition
      setRevenueData(getEmptyRevenueData(period));

      const url = `${calculate_revenue_trends}/${facilityId}?period=${period}`;
      const response = await makeRequest2(url, "GET");

      if (response && response.success) {
        let revenueApiData = [];

        // Extract data from response
        if (Array.isArray(response.data)) {
          revenueApiData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          revenueApiData = response.data.data;
        }

        if (revenueApiData && revenueApiData.length > 0) {
          const processedApiData = revenueApiData.map(item => ({
            name: item.name || "Unknown",
            revenue: item.revenue || 0,
            forecast: item.forecast || Math.round((item.revenue || 0) * 1.15),
            projected: !!item.projected
          }));

          // Merge with empty template to ensure all periods are present
          const emptyTemplate = getEmptyRevenueData(period);
          const mergedData = mergeRevenueData(emptyTemplate, processedApiData);

          if (period === revenuePeriod) {
            setRevenueData(mergedData);
          }
        }
      }
    } catch (error) {
      console.error(`Error fetching revenue data for ${period}:`, error);
    } finally {
      setRevenueLoading(false);
    }
  };

  const mergeRevenueData = (emptyTemplate, apiData) => {
    const apiDataMap = {};
    apiData.forEach(item => {
      apiDataMap[item.name] = item;
    });

    return emptyTemplate.map(templateItem => {
      const apiItem = apiDataMap[templateItem.name];
      if (apiItem) {
        return {
          ...templateItem,
          revenue: apiItem.revenue,
          forecast: apiItem.forecast,
          projected: apiItem.projected !== undefined ? apiItem.projected : templateItem.projected
        };
      }
      return templateItem;
    });
  };

  const handleCheckoutSuccess = async (checkoutData) => {
    toastify("Checkout completed successfully", "success");

    try {
      setShowAnimation(true);
      await Promise.all([
        handleRevenuePeriodChange(revenuePeriod),
        fetchRecentReservations(),
        fetchDashboardStats()
      ]);
    } catch (error) {
      console.error("Error updating dashboard after checkout:", error);
    }
  };

  const getEmptyRevenueData = (period = 'monthly') => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    if (period === 'monthly') {
      return Array(6).fill().map((_, i) => {
        const monthIdx = (currentMonth - 5 + i + 12) % 12;
        const year = currentYear - (monthIdx > currentMonth ? 1 : 0);
        return {
          name: `${months[monthIdx]} ${year}`,
          revenue: 0,
          forecast: 0,
          projected: i >= 4
        };
      });
    } else {
      const currentQuarter = Math.floor(currentMonth / 3) + 1;
      return Array(6).fill().map((_, i) => {
        let quarter = (currentQuarter - 5 + i + 4) % 4;
        if (quarter === 0) quarter = 4;
        const year = currentYear - Math.floor((5 - i - (currentQuarter - 1)) / 4);
        return {
          name: `Q${quarter} ${year}`,
          revenue: 0,
          forecast: 0,
          projected: i >= 4
        };
      });
    }
  };

  const generateFallbackReservationTrend = (period = 'thisWeek') => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    // Generate stable initial data based on day of week
    const currentDate = new Date();
    const currentDay = currentDate.getDay();
    const daysSinceSunday = currentDay === 0 ? 0 : currentDay;

    const trendData = days.map((day, index) => {
      const dayFactor = index >= 5 ? 1.5 : 1;
      const timeFactor = index < daysSinceSunday ? 1 : 0.3;

      return {
        name: day,
        reserved: Math.floor(3 * dayFactor * timeFactor),
        booked: Math.floor(5 * dayFactor * timeFactor),
        completed: Math.floor(2 * dayFactor * timeFactor)
      };
    });

    if (period === 'lastWeek') {
      return trendData.map(day => ({
        ...day,
        reserved: day.reserved * 1.2,
        booked: day.booked * 1.1,
        completed: day.completed * 1.3
      }));
    } else if (period === 'last30Days') {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const currentDate = new Date();

      return Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(currentDate.getDate() - (29 - i));

        const dayValue = (date.getDate() % 5) + 1;
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const weekendFactor = isWeekend ? 1.5 : 1;

        return {
          name: `${date.getDate()} ${months[date.getMonth()]}`,
          reserved: Math.floor(dayValue * weekendFactor),
          booked: Math.floor((dayValue + 2) * weekendFactor),
          completed: Math.floor((dayValue - 1) * weekendFactor)
        };
      });
    }

    return trendData;
  };

  function normalizeId(id) {
    if (!id) return '';

    if (typeof id === 'object') {
      return id._id ? id._id.toString() : id.toString();
    }

    return id.toString();
  }

  // Fetch apartments using the correct endpoint
  const fetchUnitsSummary = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      // Use the correct endpoint for getting all apartments and booking properties
      const response = await makeRequest2(
        `${getAllBookingPropertiesURL}/${facilityId}`,
        "GET"
      );

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch apartments");
      }

      let unitsData = [];

      // Handle the response format from get_all_apartments_and_booking_properties
      if (Array.isArray(response.data)) {
        unitsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        unitsData = response.data.data;
      }

      // Format apartments data from the API response
      const formattedUnits = unitsData.map(item => ({
        unit: {
          id: item.unit?.id || item.unit?._id,
          name: item.unit?.name || "Unknown apartment",
          unitType: item.unit?.unitType || "Unknown",
          division: item.unit?.division || "-",
          floorUnitNo: item.unit?.floorUnitNo,
          status: item.unit?.status || "Available",
          homeOwnerId: item.unit?.homeOwnerId,
          isListedForBooking: item.unit?.isListedForBooking || false
        },
        bookingProperty: item.bookingProperty ? {
          id: item.bookingProperty.id || item.bookingProperty._id,
          propertyName: item.bookingProperty.propertyName,
          propertyType: item.bookingProperty.propertyType,
          basePrice: item.bookingProperty.basePrice || 0,
          currencyId: item.bookingProperty.currencyId,
          isListed: item.bookingProperty.isListed,
          status: item.bookingProperty.status,
          managedByLandlord: item.bookingProperty.managedByLandlord || false,
          commission: item.bookingProperty.commission,
          minimumStay: item.bookingProperty.minimumStay,
          maximumStay: item.bookingProperty.maximumStay,
          cancellationPolicy: item.bookingProperty.cancellationPolicy,
          weekendPriceAdjustment: item.bookingProperty.weekendPriceAdjustment,
          advanceBookingDays: item.bookingProperty.advanceBookingDays,
          amenities: item.bookingProperty.amenities,
          description: item.bookingProperty.description
        } : null
      }));

      setUnits(formattedUnits);

      // Count apartments that are listed for booking
      const listedUnits = formattedUnits.filter(unit =>
        unit.bookingProperty && unit.bookingProperty.isListed === true
      ).length;

      const totalUnits = formattedUnits.length;
      const occupancyRate = totalUnits > 0 ? Math.round((listedUnits / totalUnits) * 100) : 0;

      setStats(prev => ({
        ...prev,
        totalUnits,
        listedUnits,
        occupancyRate
      }));

      // Create pie chart data
      setPieData([
        { name: 'Listed', value: listedUnits, color: '#4CAF50' },
        { name: 'Available (Not Listed)', value: totalUnits - listedUnits, color: '#FFC107' }
      ]);

    } catch (error) {
      console.error("Error fetching apartments summary:", error);
    }
  };

  const fetchReservationTrend = async (period = 'thisWeek') => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      const response = await makeRequest2(
        `${getReservationTrendURL}/${facilityId}?period=${period}`,
        "GET"
      );

      if (response.success && response.data) {
        let trendData = [];

        if (Array.isArray(response.data)) {
          trendData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          trendData = response.data.data;
        }

        if (trendData.length > 0) {
          setReservationTrend(trendData);
          return;
        }
      }

      // Fallback to sample data if API fails
      setReservationTrend(generateFallbackReservationTrend(period));
    } catch (error) {
      console.error("Error fetching reservation trend:", error);
      setReservationTrend(generateFallbackReservationTrend(period));
    }
  };

  // Fetch recent reservations using the correct endpoint
  const fetchRecentReservations = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getAllReservationsURL}/${facilityId}?limit=10&page=1`,
        "GET"
      );

      if (response.success) {
        let reservationsData = [];

        // Handle various response formats
        if (Array.isArray(response.data)) {
          reservationsData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          reservationsData = response.data.data;
        }

        setReservations(reservationsData);

        setShowAnimation(true);
      }
    } catch (error) {
      console.error("Error fetching recent reservations:", error);
    }
  };

  // Animate counter
  const animateCounter = (targetValue, statName, duration = 1500) => {
    if (targetValue === undefined || targetValue === null || isNaN(targetValue)) {
      targetValue = 0;
    } else {
      targetValue = Number(targetValue);
    }

    const startValue = animatedStats[statName] || 0;

    if (startValue === targetValue) {
      return;
    }

    const startTime = Date.now();

    const updateCounter = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;

      if (elapsedTime < duration) {
        const progress = elapsedTime / duration;
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.round(startValue + (targetValue - startValue) * easedProgress);

        setAnimatedStats(prev => ({
          ...prev,
          [statName]: currentValue
        }));

        requestAnimationFrame(updateCounter);
      } else {
        setAnimatedStats(prev => ({
          ...prev,
          [statName]: targetValue
        }));
      }
    };

    requestAnimationFrame(updateCounter);
  };

  // Load all data
  const loadDashboardData = async () => {
    setLoading(true);

    try {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        setLoading(false);
        return;
      }

      // Load data sequentially to avoid race conditions
      await fetchCurrencies(facilityId);
      await fetchDashboardStats();
      await fetchUnitsSummary();
      await fetchRecentReservations();
      await fetchReservationTrend('thisWeek');
      await handleRevenuePeriodChange(revenuePeriod);

      setShowAnimation(true);
    } catch (error) {
      console.error("Error loading dashboard data:", error);

      let errorMessage = "Failed to load dashboard data";
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      toastify(errorMessage, "error");

      // Set fallback data for critical components
      setRevenueData(getEmptyRevenueData(revenuePeriod));
      setReservationTrend(generateFallbackReservationTrend());
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Navigate to page
  const navigateTo = (path, params = {}) => {
    if (params && Object.keys(params).length > 0) {
      const queryString = Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
      navigate(`${path}?${queryString}`);
    } else {
      navigate(path);
    }
  };

  const hasScrollableContent = (element) => {
    return element.scrollHeight > element.clientHeight;
  };

  const updateScrollableStatus = () => {
    const cardBodies = document.querySelectorAll('.dashboard-card-body');

    cardBodies.forEach(body => {
      if (hasScrollableContent(body)) {
        body.classList.add('is-scrollable');
      } else {
        body.classList.remove('is-scrollable');
      }
    });
  };

  // Animate stats when they change
  useEffect(() => {
    if (!loading && showAnimation) {
      animateCounter(stats.totalUnits, "totalUnits");
      animateCounter(stats.listedUnits, "listedUnits");
      animateCounter(stats.totalReservations, "totalReservations");
      animateCounter(stats.pendingReservations, "pendingReservations");
      animateCounter(stats.confirmedReservations, "confirmedReservations");
      animateCounter(stats.cancelledReservations, "cancelledReservations");
      animateCounter(stats.completedReservations, "completedReservations");
      animateCounter(stats.occupancyRate, "occupancyRate");
      animateCounter(stats.bookedUnitsCount, "bookedUnitsCount");

      const timeoutId = setTimeout(() => {
        setShowAnimation(false);
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [stats, loading, showAnimation]);

  // Load data on component mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        await loadDashboardData();

        if (isMounted) {
          setShowAnimation(true);
        }
      } catch (error) {
        console.error("Error in initial data load:", error);

        if (isMounted) {
          toastify("Error loading data. Please refresh the page.", "error");
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        handleRevenuePeriodChange(revenuePeriod);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [revenuePeriod, loading]);

  useEffect(() => {
    if (!loading) {
      setTimeout(updateScrollableStatus, 300);

      window.addEventListener('resize', updateScrollableStatus);

      return () => {
        window.removeEventListener('resize', updateScrollableStatus);
      };
    }
  }, [loading, reservations, units, reservationTrend]);

  // Custom Tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label"><strong>{label}</strong></p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color || entry.stroke }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const RevenueTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label"><strong>{label}</strong></p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color || entry.stroke }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Progress Circle component
  const ProgressCircle = ({ percentage, size = 120, strokeWidth = 10 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const dash = (percentage * circumference) / 100;

    return (
      <div className="progress-circle">
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#4CAF50"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={circumference - dash}
            style={{ transition: 'stroke-dashoffset 1.5s ease-in-out' }}
          />
        </svg>
        <div className="progress-circle-text">
          {percentage}%
        </div>
      </div>
    );
  };

  // Stat Card component
  const StatCard = ({ icon, title, value, color = 'primary', onClick }) => {
    return (
      <div className={`stat-card bg-white border-${color}`} onClick={onClick}>
        <div className="stat-card-content">
          <div className={`stat-icon-wrapper bg-${color}-light`}>
            <i className={`ti ti-${icon} text-${color}`}></i>
          </div>
          <div className="stat-details">
            <h6 className="stat-title">{title}</h6>
            <h3 className="stat-value">{value}</h3>
          </div>
        </div>
      </div>
    );
  };

  // Reservation status badge component
  const StatusBadge = ({ status, checkoutPending }) => {
    const statusMap = {
      'reserved': { bg: 'bg-warning', text: 'Reserved', textColor: '#fff' },
      'booked': { bg: 'bg-success', text: 'Booked', textColor: '#fff' },
      'canceled': { bg: 'bg-danger', text: 'Canceled', textColor: '#fff' },
      'new': { bg: 'bg-warning', text: 'Reserved', textColor: '#fff' },
      'confirmed': { bg: 'bg-success', text: 'Booked', textColor: '#fff' },
      'cancelled': { bg: 'bg-danger', text: 'Canceled', textColor: '#fff' },
      'completed': { bg: 'bg-info', text: 'Completed', textColor: '#fff' },
    };

    const style = statusMap[status] || { bg: 'bg-secondary', text: status, textColor: '#fff' };
    const resolvedStyle = checkoutPending
      ? { bg: 'bg-warning', text: 'Pending', textColor: '#fff' }
      : style;

    return (
      <span className={`status-badge ${resolvedStyle.bg}`} style={{ color: resolvedStyle.textColor }}>
        {resolvedStyle.text}
      </span>
    );
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Booking Management</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loader-container">
          <div className="loader-card">
            <div className="spinner-container">
              <div className="spinner-grow"></div>
            </div>
            <h5 className="loader-title">Loading Dashboard Data</h5>
            <div className="loader-progress">
              <div className="progress">
                <div className="progress-bar progress-bar-striped progress-bar-animated"></div>
              </div>
            </div>
            <p className="loader-text">Please wait while we fetch your booking information...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Dashboard Header with Date Display */}
          <div className="page-header-title">
            <h3 className="mb-2">Booking Management</h3>
          </div>
          <div className="dashboard-header">
            <div className="row">
              <div className="col-12">
                <div className="date-container">
                  <div className="date-icon">
                    <i className="ti ti-calendar"></i>
                  </div>
                  <div className="date-content">
                    <span className="date-label">Today</span>
                    <h4 className="date-value">{currentDate}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Stats */}
          <div className="row stats-row">
            <div className="col-xl-3 col-md-6">
              <StatCard
                icon="building"
                title="Listed Apartments"
                value={`${animatedStats.listedUnits} / ${animatedStats.totalUnits}`}
                color="primary"
                onClick={() => navigateTo("/facility/booking_management/unit_listing")}
              />
            </div>
            <div className="col-xl-3 col-md-6">
              <StatCard
                icon="calendar-event"
                title="Total Reservations"
                value={animatedStats.totalReservations || 0}
                color="info"
                onClick={() => navigateTo("/facility/booking_management/unit_listing", { tab: "reservations" })}
              />
            </div>

            <div className="col-xl-3 col-md-6">
              <StatCard
                icon="calendar-plus"
                title="Booked Apartments"
                value={animatedStats.bookedUnitsCount || 0}
                color="warning"
                onClick={() => navigateTo("/facility/booking_management/unit_listing", { tab: "reservations" })}
              />
            </div>

            <div className="col-xl-3 col-md-6">
              <StatCard
                icon="chart-pie"
                title="Occupancy Rate"
                value={`${animatedStats.occupancyRate}%`}
                color="success"
              />
            </div>
          </div>

          {/* Apartments Listed and Apartments Overview */}
          <div className="row">
            {/* Apartments Listed for Booking */}
            <div className="col-lg-8">
              <div className="dashboard-card h-96">
                <div className="dashboard-card-header">
                  <div className="chart-title-section">
                    <h5 className="chart-title">Apartments Listed for Booking</h5>
                    <p className="chart-subtitle">Available properties that can be booked</p>
                  </div>
                  <div className="chart-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigateTo("/facility/booking_management/unit_listing")}
                    >
                      <i className="ti ti-list-details me-1"></i>Manage Apartments
                    </button>
                  </div>
                </div>
                <div className="dashboard-card-body">
                  <div className="table-responsive h-100">
                    <table className="reservation-table">
                      <thead>
                        <tr>
                          <th>Apartment Name</th>
                          <th>Type</th>
                          <th>Division</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {units
                          .filter(unit => {
                            const hasBookingProperty = unit.bookingProperty !== null && unit.bookingProperty !== undefined;
                            const isListed = hasBookingProperty && unit.bookingProperty.isListed === true;
                            return hasBookingProperty && isListed;
                          })
                          .slice(0, 5)
                          .map((unit, index) => (
                            <tr key={unit.unit.id || index} className="fade-in-row" style={{ animationDelay: `${index * 0.1}s` }}>
                              <td className="unit-name">
                                {unit.unit.name}
                              </td>
                              <td>
                                {unit.unit.unitType || "N/A"}
                              </td>
                              <td>
                                {unit.unit.division || "N/A"}
                              </td>
                              <td>
                                <span className="status-badge bg-success">Listed</span>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn-action"
                                    onClick={() => openEditPropertyModal(unit)}
                                    title="Edit Property"
                                  >
                                    <i className="ti ti-edit"></i>
                                  </button>
                                  <button
                                    className="btn-action btn-success"
                                    onClick={() => openBookingModal(unit)}
                                    title="Book Now"
                                  >
                                    <i className="ti ti-calendar-plus"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        {units.filter(unit => unit.bookingProperty && unit.bookingProperty.isListed === true).length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center py-4">
                              <div className="empty-state-sm">
                                <i className="ti ti-building-off"></i>
                                <p>No apartments are currently listed for booking</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* apartments Overview */}
            <div className="col-lg-4">
              <div className="dashboard-card h-96">
                <div className="dashboard-card-header">
                  <div className="chart-title-section">
                    <h5 className="chart-title">Apartments Overview</h5>
                    <p className="chart-subtitle">Current booking status</p>
                  </div>
                </div>
                <div className="dashboard-card-body">
                  <div className="progress-circle-container">
                    <ProgressCircle percentage={animatedStats.occupancyRate} />
                    <h6 className="progress-circle-label">Listing Occupancy Rate</h6>
                  </div>
                  <div className="pie-chart-container">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          animationDuration={1500}
                          animationBegin={300}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reservation Trend and Recent Reservations */}
          <div className="row">
            {/* Reservation Trend */}
            <div className="col-lg-6">
              <div className="dashboard-card h-90">
                <div className="dashboard-card-header">
                  <div className="chart-title-section">
                    <h5 className="chart-title">Weekly Reservation Trend</h5>
                    <p className="chart-subtitle">Booking activity by day and status</p>
                  </div>
                  <div className="chart-actions">
                    <select
                      className="form-select form-select-sm"
                      onChange={handleTrendPeriodChange}
                    >
                      <option value="thisWeek">This Week</option>
                      <option value="lastWeek">Last Week</option>
                      <option value="last30Days">Last 30 Days</option>
                    </select>
                  </div>
                </div>
                <div className="dashboard-card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={reservationTrend}
                      margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: 10 }} />
                      <Line
                        type="monotone"
                        dataKey="reserved"
                        stroke="#ff9800"
                        strokeWidth={2}
                        activeDot={{ r: 8 }}
                        animationDuration={1500}
                        name="Reserved"
                      />
                      <Line
                        type="monotone"
                        dataKey="booked"
                        stroke="#4caf50"
                        strokeWidth={2}
                        animationDuration={1500}
                        animationBegin={300}
                        name="Booked"
                      />
                      <Line
                        type="monotone"
                        dataKey="completed"
                        stroke="#2196f3"
                        strokeWidth={2}
                        animationDuration={1500}
                        animationBegin={600}
                        name="Completed"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Recent Reservations */}
            <div className="col-lg-6">
              <div className="dashboard-card h-90">
                <div className="dashboard-card-header">
                  <div className="chart-title-section">
                    <h5 className="chart-title">Recent Reservations</h5>
                    <p className="chart-subtitle">Latest guest bookings</p>
                  </div>
                  {stats.totalReservations > 0 && (
                    <div className="chart-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigateTo("/facility/booking_management/unit_listing", { tab: "reservations" })}
                      >
                        <i className="ti ti-list-details me-1"></i>View All
                      </button>
                    </div>
                  )}
                </div>
                <div className="dashboard-card-body">
                  {reservations.length > 0 ? (
                    <div className="table-responsive h-100">
                      <table className="reservation-table">
                        <thead>
                          <tr>
                            <th>Guest</th>
                            <th>Apartment</th>
                            <th>Check-in</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservations.slice(0, 5).map((reservation, index) => {
                            const isActive = normalizeReservationStatus(reservation.status) === 'booked';

                            return (
                              <tr
                                key={reservation._id || index}
                                className={`fade-in-row ${reservation.status === 'completed' ? 'bg-gray-100 opacity-70' : ''}`}
                                style={{ animationDelay: `${index * 0.1}s` }}
                              >
                                <td className="guest-cell">
                                  <div className="guest-info">
                                    <span className="guest-avatar">{(reservation.guestInfo?.name || 'Guest').charAt(0)}</span>
                                    <span className="guest-name">{reservation.guestInfo?.name || 'Guest'}</span>
                                  </div>
                                </td>
                                <td>
                                  {reservation.unitId?.name ||
                                    (typeof reservation.unitId === 'string' && units.find(u =>
                                      normalizeId(u.unit.id) === normalizeId(reservation.unitId))?.unit.name) ||
                                    (reservation.bookingPropertyId?.propertyName) ||
                                    'Unknown apartment'}
                                </td>
                                <td>
                                  {formatDate(reservation.checkIn)}
                                </td>
                                <td>
                                  <StatusBadge status={reservation.status} checkoutPending={reservation.checkoutPending} />
                                </td>
                                <td>
                                  <div className="flex space-x-2">
                                    <button
                                      className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none"
                                      onClick={() => openDetailsModal(reservation)}
                                      title="View Details"
                                    >
                                      <i className="ti ti-eye"></i>
                                    </button>

                                    <button
                                      className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive
                                        ? 'bg-green-100 text-green-600 hover:bg-green-600 hover:text-white'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                      onClick={() => isActive && openCheckoutModal(reservation)}
                                      title={isActive ? "Checkout Guest" : "Cannot checkout"}
                                      disabled={!isActive}
                                    >
                                      <i className="ti ti-logout"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        <i className="ti ti-calendar-off"></i>
                      </div>
                      <h6 className="empty-state-title">No Reservations Yet</h6>
                      <p className="empty-state-description">Reservations will appear here once guests start booking your properties.</p>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigateTo("/facility/booking_management/unit_listing")}
                      >
                        <i className="ti ti-plus me-2"></i>Create Reservation
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Overview */}
          <div className="row">
            <div className="col-12">
              <div className="dashboard-card">
                <div className="dashboard-card-header">
                  <div className="chart-title-section">
                    <h5 className="chart-title">Revenue Overview</h5>
                    <p className="chart-subtitle">
                      {revenuePeriod === 'monthly'
                        ? 'Monthly income performance and projections'
                        : 'Quarterly income performance and projections'}
                    </p>
                  </div>
                  <div className="chart-actions">
                    <div className="btn-group btn-group-sm">
                      <button
                        className={`btn btn-outline-primary ${revenuePeriod === 'monthly' ? 'active' : ''}`}
                        onClick={() => {
                          if (revenuePeriod !== 'monthly') {
                            setRevenueData(getEmptyRevenueData('monthly'));
                            setRevenuePeriod('monthly');
                          }
                        }}
                        disabled={revenueLoading}
                      >
                        Monthly
                      </button>
                      <button
                        className={`btn btn-outline-primary ${revenuePeriod === 'quarterly' ? 'active' : ''}`}
                        onClick={() => {
                          if (revenuePeriod !== 'quarterly') {
                            setRevenueData(getEmptyRevenueData('quarterly'));
                            setRevenuePeriod('quarterly');
                          }
                        }}
                        disabled={revenueLoading}
                      >
                        Quarterly
                      </button>
                    </div>
                  </div>
                </div>
                <div className="dashboard-card-body">
                  {revenueLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '320px' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : revenueData && revenueData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <AreaChart
                        data={revenueData}
                        margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                      >
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4361ee" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#4361ee" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3bc14a" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#3bc14a" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="name" tickLine={false} axisLine={false} />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => value.toLocaleString()}
                        />
                        <Tooltip content={<RevenueTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: 10 }} />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stroke="#4361ee"
                          fillOpacity={1}
                          fill="url(#colorRevenue)"
                          animationDuration={1500}
                          activeDot={{ r: 8 }}
                          name="Actual Revenue"
                        />
                        <Area
                          type="monotone"
                          dataKey="forecast"
                          stroke="#3bc14a"
                          fillOpacity={1}
                          fill="url(#colorForecast)"
                          animationDuration={1500}
                          strokeDasharray="5 5"
                          name="Projected Revenue"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ height: '320px' }}>
                      <i className="ti ti-chart-bar text-muted" style={{ fontSize: '3rem' }}></i>
                      <h6 className="mt-3 mb-1">No Revenue Data Available</h6>
                      <p className="text-muted">Complete a checkout to start tracking revenue</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Cards - apartments and Reservations */}
          <div className="row">
            <div className="col-12 mb-4">
              <div className="section-header">
                <h5 className="section-title">Quick Actions</h5>
                <hr className="section-divider" />
              </div>
            </div>

            <div className="col-md-6">
              <div className="action-card action-card-primary" onClick={() => navigateTo("/facility/booking_management/unit_listing")}>
                <div className="action-card-body">
                  <div className="action-icon primary">
                    <i className="ti ti-building"></i>
                  </div>
                  <h5 className="action-title">Manage Apartments</h5>
                  <p className="action-description">
                    List or unlist your properties on booking platforms
                  </p>
                  <button className="btn btn-primary action-button">
                    <i className="ti ti-stack me-2"></i>View All Apartments
                  </button>
                </div>
                <div className="action-card-progress">
                  <div className="progress">
                    <div
                      className="progress-bar bg-primary"
                      style={{ width: `${animatedStats.listedUnits / Math.max(animatedStats.totalUnits, 1) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="action-card action-card-warning" onClick={() => navigateTo("/facility/booking_management/unit_listing", { tab: "reservations" })}>
                <div className="action-card-body">
                  <div className="action-icon warning">
                    <i className="ti ti-calendar-event"></i>
                  </div>
                  <h5 className="action-title">Reservations</h5>
                  <p className="action-description">
                    Manage all your bookings and guest reservations
                  </p>
                  <button className="btn btn-warning action-button">
                    <i className="ti ti-calendar-stats me-2"></i>View Reservations
                  </button>
                </div>
                <div className="action-card-progress">
                  <div className="progress">
                    <div
                      className="progress-bar bg-warning"
                      style={{ width: `${animatedStats.pendingReservations / Math.max(animatedStats.totalReservations, 1) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </>
      )}

      {showBookingModal && selectedUnit && (
        <BookingModal
          visible={showBookingModal}
          onHide={() => setShowBookingModal(false)}
          unit={selectedUnit}
          onSuccess={handleBookingSuccess}
        />
      )}

      {showCheckoutModal && selectedReservation && (
        <CheckoutModal
          visible={showCheckoutModal}
          onHide={() => setShowCheckoutModal(false)}
          reservation={selectedReservation}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {showDetailsModal && selectedReservationId && (
        <ReservationDetailsModal
          visible={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          reservationId={selectedReservationId}
        />
      )}

      {showEditPropertyModal && selectedPropertyToEdit && (
        <Dialog
          header="Edit Booking Property"
          visible={showEditPropertyModal}
          style={{ width: '80vw', maxWidth: '900px' }}
          onHide={() => setShowEditPropertyModal(false)}
          closeOnEscape={false}
          closable={true}
          modal={true}
        >
          <EditBookingPropertyForm
            visible={showEditPropertyModal}
            onHide={() => setShowEditPropertyModal(false)}
            unit={selectedPropertyToEdit}
            facilityId={facilityIdForEdit}
            currencies={currencies.length > 0 ? currencies : [
              { _id: "default", name: "Default Currency", symbol: "$" }
            ]}
            onSuccess={handleEditPropertySuccess}
          />
        </Dialog>
      )}
    </Layout>
  );
};

export default BookingDashboard;
